import capitalize from 'lodash/capitalize'
import { createMRTColumnHelper, MRT_ColumnDef } from 'material-react-table'
import { useMemo } from 'react'

import { getMrtTableDisabled, SelectCell } from '@/components'
import { createHeaderWithTooltip } from '@/components/MrtTable/helpers'
import { SystemUser } from '@/endpoints/models'
import { useAppContext } from '@/hooks'
import { UpdateCellValueHandler } from '@/hooks/useTableHandlers'
import { useDetailTabContext } from '@/pages/User/pages/Home/components/DetailTab'
import { ViewPermissionEnum } from '@/rtkApi/mddApiSliceGeneratedV2'

import { ViewPermissionFlat } from './PermissionsV2'

type UseViewPermissionsColumnsV2Props = {
	systemUsers: SystemUser[] | undefined
	updateCellValue: UpdateCellValueHandler<ViewPermissionFlat>
}

// MDD-2606: View element permits only Select and Alter (no INSERT, UPDATE, DELETE)
const viewPermissions: ViewPermissionEnum[] = ['SELECT', 'ALTER']

const mapPermissionToOption = (value: string) => ({
	value,
	label: capitalize(value),
})

export const useViewPermissionsColumnsV2 = ({
	systemUsers,
	updateCellValue,
}: UseViewPermissionsColumnsV2Props) => {
	const { t } = useAppContext()
	const {
		state: { editMode },
	} = useDetailTabContext()

	const columnHelper = createMRTColumnHelper<ViewPermissionFlat>()

	const columns = useMemo(
		() => [
			columnHelper.accessor('userId', {
				...createHeaderWithTooltip(t('SYSTEM_USER')),
				size: 250,
				minSize: 150,
				meta: {
					optionsSource: systemUsers ?? [],
					optionValue: 'id',
					optionLabel: 'name',
					isNumeric: true,
				},
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<ViewPermissionFlat>({
						editMode,
						row,
						column,
					})

					return (
						<SelectCell<ViewPermissionFlat>
							cell={cell}
							column={column}
							table={table}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
						/>
					)
				},
			}),
			columnHelper.accessor('permissions', {
				...createHeaderWithTooltip(t('PERMISSIONS')),
				size: 200,
				minSize: 120,
				meta: {
					optionsSource: viewPermissions.map(mapPermissionToOption),
					optionValue: 'value',
					optionLabel: 'label',
				},
				enableColumnActions: false,
				enableSorting: false,
				Edit: ({ cell, table, row, column }) => {
					const isDisabled = getMrtTableDisabled<ViewPermissionFlat>({
						editMode,
						row,
						column,
					})

					return (
						<SelectCell<ViewPermissionFlat>
							cell={cell}
							column={column}
							table={table}
							row={row}
							updateCellValue={updateCellValue}
							isDisabled={isDisabled}
							isMulti
						/>
					)
				},
			}),
		],
		[columnHelper, editMode, systemUsers, t, updateCellValue],
	)

	return columns as MRT_ColumnDef<ViewPermissionFlat>[]
}
